import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Button from "../Button/Button";
import "./CookieConsent.css";

const STORAGE_KEY = "juristai-cookie-consent";
const OPEN_EVENT = "juristai:open-cookie-preferences";
const CONSENT_UPDATED_EVENT = "juristai:cookie-consent-updated";
const CONSENT_VERSION = "2026-03-09";
const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

const defaultConsent = {
  version: CONSENT_VERSION,
  essential: true,
  analytics: true,
  marketingPixels: false,
  updatedAt: null,
};

const readConsent = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue);
    if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
      return null;
    }

    const expiresAt = Number(parsedValue.expiresAt);
    if (Number.isFinite(expiresAt) && expiresAt <= Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Preserve choices saved before expiration was introduced. Start their
    // six-month lifetime when this version first reads them.
    if (!Number.isFinite(expiresAt)) {
      parsedValue.expiresAt = Date.now() + CONSENT_MAX_AGE_MS;
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(parsedValue));
    }

    return {
      ...defaultConsent,
      ...parsedValue,
      essential: true,
      version: CONSENT_VERSION,
    };
  } catch (error) {
    return null;
  }
};

const persistConsent = (consent) => {
  if (typeof window === "undefined") {
    return;
  }

  const nextConsent = {
    ...defaultConsent,
    ...consent,
    essential: true,
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    expiresAt: Date.now() + CONSENT_MAX_AGE_MS,
  };

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextConsent));
  window.dispatchEvent(
    new CustomEvent(CONSENT_UPDATED_EVENT, {
      detail: nextConsent,
    })
  );
};

const CookieConsent = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(
    () => !location.pathname.startsWith("/dashboard") && !readConsent()
  );

  const isDashboardRoute = useMemo(
    () => location.pathname.startsWith("/dashboard"),
    [location.pathname]
  );

  useEffect(() => {
    if (isDashboardRoute) {
      setIsOpen(false);
      return undefined;
    }

    const existingConsent = readConsent();

    if (existingConsent) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }

    const handleOpenPreferences = () => {
      setIsOpen(true);
    };

    window.addEventListener(OPEN_EVENT, handleOpenPreferences);

    return () => {
      window.removeEventListener(OPEN_EVENT, handleOpenPreferences);
    };
  }, [isDashboardRoute]);

  const savePreferences = (nextConsent) => {
    persistConsent(nextConsent);
    setIsOpen(false);
  };

  const handleAcceptAll = () => {
    savePreferences({
      analytics: true,
      marketingPixels: true,
    });
  };

  const handleEssentialOnly = () => {
    savePreferences({
      analytics: false,
      marketingPixels: false,
    });
  };

  if (!isOpen || isDashboardRoute) {
    return null;
  }

  return (
    <div
      className="cookie-consent"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
    >
      <div className="cookie-consent__content">
        <div className="cookie-consent__copy">
          <h2 id="cookie-consent-title">Allow marketing cookies?</h2>
          <p className="cookie-consent__description">
            We always use essential cookies to keep Vienna secure and
            functional. With your permission, we use optional marketing cookies
            on our marketing site to power advertising and remarketing tools.
          </p>
          <p className="cookie-consent__description cookie-consent__description--small">
            Authenticated case and workspace pages do not use marketing pixels.
            {" "}
            Review the{" "}
            <Link to="/privacy-policy" onClick={() => setIsOpen(false)}>
              Privacy Policy
            </Link>{" "}
            for more detail.
          </p>
        </div>

        <div className="cookie-consent__actions">
          <Button className="button--transparent-new" onClick={handleEssentialOnly}>
            Only essential cookies
          </Button>
          <Button className="button--primary-new" onClick={handleAcceptAll}>
            Allow marketing
          </Button>
        </div>
      </div>
    </div>
  );
};

export { CONSENT_UPDATED_EVENT, OPEN_EVENT, STORAGE_KEY, readConsent };
export default CookieConsent;
